/**
 * Home page components (also used by snippets/bundle-pair on the product page):
 *   <bundle-pair>      "Add both to cart" for a Double feature pair
 *   <product-rail>     scroll-snap rail with prev/next buttons and mouse drag
 *   <quote-carousel>   crossfading testimonials with optional, pausable auto-advance
 *   <featured-gallery> featured product thumbnails and the chosen variant's photo
 *   [data-product-form] featured product add to cart through Lumi.cart
 *   [data-split-heading] one-time masked line reveal when motion is allowed
 * Every script guards itself, so including this file more than once is harmless.
 */
(function () {
  if (window.__lumiHomeLoaded) return;
  window.__lumiHomeLoaded = true;

  const Lumi = (window.Lumi = window.Lumi || {});
  const str = (key, fallback) => (Lumi.strings && Lumi.strings[key]) || fallback;
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const money = (cents) => (Lumi.formatMoney ? Lumi.formatMoney(cents) : `$${(cents / 100).toFixed(2)}`);

  function showError(el, message) {
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
  }

  /* ---------- <bundle-picker>: a listbox with thumbnails for one bundle line ---------- */
  if (!customElements.get('bundle-picker')) {
    class BundlePicker extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        this.btn = this.querySelector('[data-bpick-btn]');
        this.list = this.querySelector('[data-bpick-list]');
        this.options = Array.from(this.list.querySelectorAll('[role="option"]'));
        this.btn.addEventListener('click', () => (this.list.hidden ? this.open() : this.close()));
        this.list.addEventListener('click', (e) => {
          const opt = e.target.closest('[role="option"]');
          if (opt && opt.getAttribute('aria-disabled') !== 'true') this.choose(opt);
        });
        this.list.addEventListener('keydown', (e) => this.onKey(e));
        this.btn.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            this.open();
          }
        });
        this.onOutside = (e) => {
          if (!this.contains(e.target)) this.close();
        };
      }

      disconnectedCallback() {
        this.close();
      }

      open() {
        this.list.hidden = false;
        this.btn.setAttribute('aria-expanded', 'true');
        document.addEventListener('pointerdown', this.onOutside);
        if (Lumi.lockScroll) Lumi.lockScroll(true);
        const current = this.options.find((o) => o.getAttribute('aria-selected') === 'true') || this.options[0];
        if (current) {
          current.focus({ preventScroll: true });
          current.scrollIntoView({ block: 'nearest' });
        }
      }

      close(refocus) {
        if (this.list.hidden) return;
        this.list.hidden = true;
        this.btn.setAttribute('aria-expanded', 'false');
        document.removeEventListener('pointerdown', this.onOutside);
        if (Lumi.lockScroll && !document.querySelector('dialog[open]')) Lumi.lockScroll(false);
        if (refocus) this.btn.focus();
      }

      onKey(e) {
        const i = this.options.indexOf(document.activeElement);
        if (e.key === 'Escape') {
          e.preventDefault();
          this.close(true);
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          const next = this.options[Math.min(this.options.length - 1, Math.max(0, i + (e.key === 'ArrowDown' ? 1 : -1)))];
          if (next) next.focus({ preventScroll: true });
          if (next) next.scrollIntoView({ block: 'nearest' });
        } else if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const opt = this.options[i];
          if (opt && opt.getAttribute('aria-disabled') !== 'true') this.choose(opt);
        } else if (e.key === 'Tab') {
          this.close();
        }
      }

      // Marks the option current and mirrors it on the button. `silent` skips the change event
      // (used when a product page picker already changed the variant).
      select(value, silent) {
        const opt = this.options.find((o) => o.dataset.value === String(value));
        if (!opt) return;
        this.options.forEach((o) => o.setAttribute('aria-selected', o === opt ? 'true' : 'false'));
        const label = this.querySelector('[data-bpick-label]');
        const price = this.querySelector('[data-bpick-price]');
        const thumb = this.querySelector('[data-bpick-thumb]');
        if (label) label.textContent = opt.dataset.title;
        if (price) price.textContent = money(Number(opt.dataset.price));
        if (thumb) {
          let img = thumb.querySelector('img');
          if (opt.dataset.thumb) {
            if (!img) {
              img = document.createElement('img');
              img.alt = '';
              img.width = 44;
              img.height = 44;
              thumb.appendChild(img);
            }
            img.src = opt.dataset.thumb;
          } else if (img) {
            img.remove();
          }
        }
        if (!silent) {
          this.dispatchEvent(
            new CustomEvent('bpick:change', {
              bubbles: true,
              detail: {
                id: Number(opt.dataset.value),
                price: Number(opt.dataset.price),
                available: opt.dataset.available === 'true',
              },
            })
          );
        }
      }

      choose(opt) {
        this.select(opt.dataset.value, false);
        this.close(true);
      }
    }
    customElements.define('bundle-picker', BundlePicker);
  }

  /* ---------- <bundle-pair> ---------- */
  if (!customElements.get('bundle-pair')) {
    class BundlePair extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        this.button = this.querySelector('[data-bundle-add]');
        this.label = this.querySelector('[data-bundle-label]');
        this.error = this.querySelector('[data-bundle-error]');
        this.idsA = (this.dataset.idsA || '').split(',').filter(Boolean).map(Number);
        this.state = {
          a: Number(this.dataset.variantA),
          b: Number(this.dataset.variantB),
          priceA: Number(this.dataset.priceA),
          priceB: Number(this.dataset.priceB),
          okA: this.dataset.availableA === 'true',
          okB: this.dataset.availableB === 'true',
        };
        this.button.addEventListener('click', () => this.add());
        this.pickers = {
          a: this.querySelector('[data-bundle-variant="a"]'),
          b: this.querySelector('[data-bundle-variant="b"]'),
        };
        this.addEventListener('bpick:change', (e) => {
          const side = e.target.dataset.bundleVariant;
          if (side) this.setSide(side, e.detail.id, e.detail.price, e.detail.available);
        });
        this.onVariant = (e) => this.variantChanged(e.detail && e.detail.variant);
        document.addEventListener('lumi:variant:changed', this.onVariant);
      }

      disconnectedCallback() {
        document.removeEventListener('lumi:variant:changed', this.onVariant);
      }

      // A product page variant picker for the lead product swaps in the chosen variant and
      // keeps the block's own dropdown for side a in step.
      variantChanged(variant) {
        if (!variant || !this.idsA.includes(Number(variant.id))) return;
        this.setSide('a', Number(variant.id), Number(variant.price), !!variant.available);
        const compare = this.querySelector('[data-bundle-price="a"] .price__compare');
        if (compare) compare.textContent = money(variant.compare_at_price || variant.price);
        if (this.pickers.a && this.pickers.a.select) this.pickers.a.select(String(variant.id), true);
      }

      // The tile photos stay on each product's featured image; only the line and the total move.
      setSide(side, id, price, available) {
        this.state[side] = id;
        this.state[side === 'a' ? 'priceA' : 'priceB'] = price;
        this.state[side === 'a' ? 'okA' : 'okB'] = available;
        const priceEl = this.querySelector('[data-bundle-price="' + side + '"] .price__current');
        if (priceEl) priceEl.textContent = money(price);
        this.renderTotal();
        this.sync();
      }

      // Full price struck through, discounted pair price in front (bundle_discount_percent).
      renderTotal() {
        const sum = this.state.priceA + this.state.priceB;
        const percent = Number(this.dataset.percent) || 0;
        const current = this.querySelector('[data-bundle-total-current]');
        const compare = this.querySelector('[data-bundle-total-compare]');
        // Per line, floored to the cent: the same arithmetic as Shopify's automatic discount.
        const off = Math.floor((this.state.priceA * percent) / 100) + Math.floor((this.state.priceB * percent) / 100);
        if (current) current.textContent = money(sum - off);
        if (compare) compare.textContent = money(sum);
      }

      sync() {
        const ok = this.state.okA && this.state.okB;
        this.button.disabled = !ok;
        this.label.textContent = ok ? this.dataset.labelAdd : this.dataset.labelSoldOut;
      }

      async add() {
        if (this.button.disabled || this.button.getAttribute('aria-busy') === 'true') return;
        showError(this.error, '');
        this.button.setAttribute('aria-busy', 'true');
        try {
          await Lumi.cart.add([
            { id: this.state.a, quantity: 1 },
            { id: this.state.b, quantity: 1 },
          ]);
          Lumi.emit('lumi:cart:open');
          if (Lumi.announce) Lumi.announce(this.dataset.labelAdded || str('addedToCart', 'Added to your cart'));
        } catch (err) {
          showError(this.error, (err && err.message) || str('cartError', 'That did not work. Please try again.'));
        } finally {
          this.button.removeAttribute('aria-busy');
        }
      }
    }
    customElements.define('bundle-pair', BundlePair);
  }

  /* ---------- <product-rail> ---------- */
  if (!customElements.get('product-rail')) {
    class ProductRail extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        this.track = this.querySelector('[data-rail-track]');
        this.prev = this.querySelector('[data-rail-prev]');
        this.next = this.querySelector('[data-rail-next]');
        if (!this.track) return;
        this.sync = this.sync.bind(this);
        this.prev.addEventListener('click', () => this.step(-1));
        this.next.addEventListener('click', () => this.step(1));
        this.track.addEventListener('scroll', () => window.requestAnimationFrame(this.sync), { passive: true });
        this.ro = new ResizeObserver(this.sync);
        this.ro.observe(this.track);
        this.setupDrag();
        this.sync();
      }

      disconnectedCallback() {
        if (this.ro) this.ro.disconnect();
      }

      stepSize() {
        const item = this.track.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const one = item ? item.getBoundingClientRect().width + gap : 300;
        return Math.max(one, Math.floor(this.track.clientWidth / one) * one - gap);
      }

      step(dir) {
        this.track.scrollBy({ left: dir * this.stepSize(), behavior: reduce() ? 'auto' : 'smooth' });
      }

      sync() {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        this.prev.disabled = this.track.scrollLeft <= 2;
        this.next.disabled = this.track.scrollLeft >= max;
      }

      // Mouse drag to scroll. Touch keeps native panning; a drag never fires the card link.
      setupDrag() {
        let startX = 0;
        let startLeft = 0;
        let moved = false;
        let id = null;
        const track = this.track;
        track.addEventListener('pointerdown', (e) => {
          if (e.pointerType !== 'mouse' || e.button !== 0) return;
          if (e.target.closest('button, input, select, textarea')) return;
          id = e.pointerId;
          startX = e.clientX;
          startLeft = track.scrollLeft;
          moved = false;
        });
        track.addEventListener('pointermove', (e) => {
          if (e.pointerId !== id) return;
          const dx = e.clientX - startX;
          if (!moved && Math.abs(dx) > 6) {
            moved = true;
            this.setAttribute('data-dragging', '');
            track.setPointerCapture(id);
          }
          if (moved) track.scrollLeft = startLeft - dx;
        });
        const end = (e) => {
          if (e.pointerId !== id) return;
          id = null;
          if (!moved) return;
          // Snapping comes back on with the attribute, which settles the track on the nearest card.
          this.removeAttribute('data-dragging');
        };
        track.addEventListener('pointerup', end);
        track.addEventListener('pointercancel', end);
        track.addEventListener(
          'click',
          (e) => {
            if (moved) {
              e.preventDefault();
              e.stopPropagation();
              moved = false;
            }
          },
          true
        );
        track.addEventListener('dragstart', (e) => e.preventDefault());
      }
    }
    customElements.define('product-rail', ProductRail);
  }

  /* ---------- <quote-carousel> ---------- */
  if (!customElements.get('quote-carousel')) {
    class QuoteCarousel extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this.slides = [...this.querySelectorAll('[data-slide]')];
        if (this.slides.length < 2) return;
        this._ready = true;
        this.ticks = [...this.querySelectorAll('[data-tick]')];
        this.current = this.querySelector('[data-current]');
        this.region = this.querySelector('[data-slides]');
        this.pauseBtn = this.querySelector('[data-carousel-pause]');
        this.index = 0;
        this.delay = Number(this.dataset.autoplay) || 0;
        this.userPaused = false;
        this.hovering = false;

        this.querySelector('[data-carousel-prev]').addEventListener('click', () => this.go(this.index - 1, true));
        this.querySelector('[data-carousel-next]').addEventListener('click', () => this.go(this.index + 1, true));
        this.setAttribute('data-ready', '');
        this.go(0, false);
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => this.setAttribute('data-animate', '')));

        if (this.delay && !reduce()) {
          this.style.setProperty('--tick', `${this.delay}ms`);
          this.pauseBtn.hidden = false;
          this.pauseBtn.addEventListener('click', () => {
            this.userPaused = !this.userPaused;
            this.pauseBtn.setAttribute('aria-pressed', String(this.userPaused));
            this.pauseBtn.setAttribute('aria-label', this.userPaused ? this.pauseBtn.dataset.labelPlay : this.pauseBtn.dataset.labelPause);
            this.schedule();
          });
          this.addEventListener('pointerenter', () => {
            this.hovering = true;
            this.schedule();
          });
          this.addEventListener('pointerleave', () => {
            this.hovering = false;
            this.schedule();
          });
          this.addEventListener('focusin', () => this.schedule());
          this.addEventListener('focusout', () => window.requestAnimationFrame(() => this.schedule()));
          this.addEventListener('animationend', (e) => {
            if (e.animationName === 'lumi-tick' && this.running) this.go(this.index + 1, false);
          });
          this.io = new IntersectionObserver((entries) => {
            this.inView = entries[0].isIntersecting;
            this.schedule();
          });
          this.io.observe(this);
        }
      }

      disconnectedCallback() {
        if (this.io) this.io.disconnect();
      }

      get running() {
        return !!this.delay && !reduce() && !this.userPaused;
      }

      // The progress tick is a CSS animation; when it finishes, the next quote comes in. Pausing
      // pauses the animation, so the bar and the timing always agree.
      schedule() {
        const holding = this.hovering || this.contains(document.activeElement) || !this.inView;
        this.toggleAttribute('data-running', this.running);
        this.toggleAttribute('data-paused', this.running && holding);
        // Screen readers hear changes only when people make them.
        this.region.setAttribute('aria-live', this.running ? 'off' : 'polite');
      }

      go(next, manual) {
        const n = this.slides.length;
        this.index = (next + n) % n;
        this.slides.forEach((slide, i) => {
          const active = i === this.index;
          slide.toggleAttribute('data-active', active);
          slide.setAttribute('aria-hidden', String(!active));
        });
        this.ticks.forEach((tick, i) => {
          tick.toggleAttribute('data-active', i === this.index);
          tick.toggleAttribute('data-done', i < this.index);
          // Restart the progress animation on the new active tick.
          const bar = tick.firstElementChild;
          if (bar && i === this.index) {
            bar.style.animation = 'none';
            void bar.offsetWidth;
            bar.style.animation = '';
          }
        });
        if (this.current) this.current.textContent = String(this.index + 1);
        this.schedule();
        if (manual && this.region) this.region.setAttribute('aria-live', 'polite');
      }
    }
    customElements.define('quote-carousel', QuoteCarousel);
  }

  /* ---------- <featured-gallery>: thumbnails switch the photo, which follows the chosen variant ---------- */
  if (!customElements.get('featured-gallery')) {
    class FeaturedGallery extends HTMLElement {
      connectedCallback() {
        if (!this._ready) {
          this._ready = true;
          this.slides = [...this.querySelectorAll('[data-fg-slide]')];
          this.thumbs = [...this.querySelectorAll('[data-fg-thumb]')];
          this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.show(i)));
          // Same as quick view: only this section's picker, on to the variant's featured photo.
          this.onVariant = (e) => {
            const scope = this.closest('[data-section-type="featured-product"]') || this;
            if (!e.detail || e.detail.picker == null || !scope.contains(e.detail.picker)) return;
            const media = e.detail.variant && e.detail.variant.featured_media;
            if (media) this.show(this.slides.findIndex((s) => s.dataset.mediaId === String(media.id)));
          };
        }
        document.addEventListener('lumi:variant:changed', this.onVariant);
      }

      disconnectedCallback() {
        document.removeEventListener('lumi:variant:changed', this.onVariant);
      }

      show(index) {
        if (index < 0 || index >= this.slides.length) return;
        this.slides.forEach((slide, i) => (slide.hidden = i !== index));
        this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === index ? 'true' : 'false'));
        // Bring the current thumb into its rail without scrolling the page.
        const thumb = this.thumbs[index];
        const rail = thumb && thumb.parentElement;
        if (!rail) return;
        rail.scrollTo({
          left: thumb.offsetLeft - (rail.clientWidth - thumb.offsetWidth) / 2,
          top: thumb.offsetTop - (rail.clientHeight - thumb.offsetHeight) / 2,
          behavior: reduce() ? 'auto' : 'smooth',
        });
      }
    }
    customElements.define('featured-gallery', FeaturedGallery);
  }

  /* ---------- Featured product form ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest && e.target.closest('[data-section-type="featured-product"] form[data-product-form]');
    if (!form || !Lumi.cart) return;
    e.preventDefault();
    const button = form.querySelector('[data-add-button]');
    const error = form.querySelector('[data-form-error]');
    const id = Number(form.querySelector('input[name="id"]').value);
    if (!id || (button && button.disabled)) return;
    showError(error, '');
    if (button) button.setAttribute('aria-busy', 'true');
    try {
      await Lumi.cart.add([{ id, quantity: 1 }]);
      Lumi.emit('lumi:cart:open');
      if (Lumi.announce) Lumi.announce(str('addedToCart', 'Added to your cart'));
    } catch (err) {
      showError(error, (err && err.message) || str('cartError', 'That did not work. Please try again.'));
    } finally {
      if (button) button.removeAttribute('aria-busy');
    }
  });

  /* ---------- Masked line reveal for section headings ---------- */
  function splitReveal() {
    const headings = document.querySelectorAll('[data-split-heading]');
    if (!headings.length || !Lumi.motion || !window.SplitText) return;
    Lumi.motion.register('split-headings', () => {
      const gsap = window.gsap;
      gsap.registerPlugin(window.SplitText);
      const splits = [];
      // One observer plays each heading's reveal the first time it comes into view.
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const heading = entry.target;
            heading.dataset.revealed = '';
            if (heading.__reveal) heading.__reveal.play();
            io.unobserve(heading);
          });
        },
        { rootMargin: '0px 0px -12% 0px' }
      );
      headings.forEach((heading) => {
        // Only headings still below the fold: nothing already on screen ever hides.
        if (heading.getBoundingClientRect().top < window.innerHeight * 0.9) return;
        const split = window.SplitText.create(heading, {
          type: 'lines',
          mask: 'lines',
          linesClass: 'split-line',
          autoSplit: true,
          onSplit(self) {
            const tween = gsap.from(self.lines, { yPercent: 105, duration: 1.1, stagger: 0.09, ease: 'power4.out', paused: true });
            heading.__reveal = tween;
            if (heading.dataset.revealed !== undefined) tween.play();
            return tween;
          },
        });
        splits.push(split);
        io.observe(heading);
      });
      return () => {
        io.disconnect();
        splits.forEach((s) => s.revert());
      };
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', splitReveal);
  else splitReveal();
})();
